import { CircularProgress } from '@material-ui/core';
import ClearIcon from '@mui/icons-material/Clear';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import { ServiceFieldSettings } from '@onlog-public/additional-services-types';
import TextDisplayField from '@pages/AdditionalServices/components/FieldDisplay/AdditionServiceField/TextDisplayField';
import { SelectOption } from '@pages/AdditionalServices/containers/services/optionsGenerator/types';
import clsx from 'clsx';
import React, { FC, useMemo } from 'react';

export interface CurrencySelectFieldProps {
  fieldCode: string;
  value?: number;
  settings: ServiceFieldSettings;
  isFieldsDisabled: boolean;
  tooltip: string;
  placeholder: string;
  options?: SelectOption[];
  isOptionsLoading: boolean;
  error?: string;
  isExportMode: boolean;

  onChange: (value: number) => void;
}

const CurrencySelectField: FC<CurrencySelectFieldProps> = (props) => {
  const {
    fieldCode,
    value,
    settings,
    tooltip,
    placeholder,
    options = [],
    error = '',
    isOptionsLoading,
    isFieldsDisabled,
    isExportMode,
    onChange,
  } = props;

  const fieldSize = settings.FieldSize === 'small' ? 'small' : 'medium';
  const loaderSize = settings.FieldSize === 'small' ? 16 : 24;
  const isWithDescription = !!options.find((o) => !!o.description);
  const existOption = options.find((o) => o.value === value);
  const sortedOptions: SelectOption[] = useMemo(() => {
    const startOptions = settings.CurrencyFieldSettings.StartListOrderElements.map((el) =>
      options.find((option) => option.value.toString() === el)
    ).filter(Boolean);
    const restOptions = options.filter(
      (option) =>
        !settings.CurrencyFieldSettings.StartListOrderElements.includes(option.value.toString())
    );

    if (settings.CurrencyFieldSettings.ItemsSortDirection === 'asc') {
      restOptions.sort((a, b) => (a.label > b.label ? 1 : -1));
    }

    if (settings.CurrencyFieldSettings.ItemsSortDirection === 'desc') {
      restOptions.sort((a, b) => (a.label < b.label ? 1 : -1));
    }

    return [...startOptions, ...restOptions];
  }, [settings.CurrencyFieldSettings.StartListOrderElements, options]);

  if (settings.IsNeedDisplayOnlyTextInfo) {
    return <TextDisplayField tooltip={tooltip} value={String(existOption?.label ?? '')} />;
  }

  return (
    <Tooltip
      placement="top"
      title={
        tooltip.length > 0 && (
          <div dangerouslySetInnerHTML={{ __html: tooltip }} className="a-html-content" />
        )
      }
    >
      <FormControl variant="outlined" fullWidth={true} error={error.length > 0}>
        <InputLabel
          id={`${fieldCode}-label`}
          className={clsx({ 'a-service-field-label': isExportMode })}
        >
          {placeholder}
        </InputLabel>
        <Select
          labelId={`${fieldCode}-label`}
          id={`${fieldCode}-select`}
          value={String(existOption?.value || '')}
          className={clsx('a-service-field-selector', fieldSize, {
            'with-description': isWithDescription,
          })}
          fullWidth
          size={fieldSize}
          label={!isExportMode ? placeholder : undefined}
          disabled={isFieldsDisabled || settings.IsNeedDisableField}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          }}
          endAdornment={
            <>
              {isOptionsLoading && (
                <div className={clsx('a-service-field-selector-loader', fieldSize)}>
                  <CircularProgress color="inherit" size={loaderSize} />
                </div>
              )}
              <IconButton
                className="a-service-field-selector-clear-btn"
                aria-label="clear"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(0);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </>
          }
          onChange={(e) => onChange(parseInt(e.target.value))}
        >
          {sortedOptions.map((option) => (
            <MenuItem
              value={option.value}
              key={option.value}
              className="a-service-field-selector-option--root"
            >
              <div
                className={clsx('a-service-field-selector-option', {
                  'with-description': isWithDescription,
                  [fieldSize]: true,
                })}
              >
                <div className="option-label">{option.label}</div>
                {option.description && (
                  <div className="option-description">{option.description}</div>
                )}
              </div>
            </MenuItem>
          ))}
        </Select>
        {error.length > 0 && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    </Tooltip>
  );
};

export default CurrencySelectField;
